/* Copyright (c) 2020-present CloudIO Inc */

import {
  useStateXGetter,
  useStateXSetter,
  useStateXWritableSelector,
} from '@cloudio-saas/statex';
import { Badge, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { AnyItem, AnyManager, Page } from 'componentregistry';
import { activeItemSelector } from 'designer/activeItemSelector';
import { designPath } from 'designer/atoms';
import { genId } from 'designer/state';
import useDnD from 'designer/useDnD';
import { useIORef } from 'designer/useIORef';
import { motion } from 'framer-motion';
import useMounted from 'hooks/useMounted';
import Icon from 'icons/Icon';
import {
  sidebarActiveComponentAtom,
  sidebarComponentViewAtom,
} from 'layout/Sidebar/atoms';
import { usePlatform } from 'platform/PlatformHooks';
import React, { startTransition, useState } from 'react';
import { createItem } from 'registry/Components';

const useStyles = makeStyles((theme: Theme) => ({
  comp: {
    flex: 1,
    flexBasis: '90px',
    display: 'flex',
    height: 90,
    width: 90,
    margin: 4,
    border: `1px dotted ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    textAlign: 'center',
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    userSelect: 'none',
    cursor: 'pointer',
  },
  devMode: {
    cursor: 'move',
  },
  name: {
    flex: '0 0 2.4em',
    width: '100%',
    lineHeight: '2.4em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: '0 8px',
  },
  icon: {
    height: 'calc(100% - 2.4em)',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

interface Props {
  appUid: string;
  devMode: boolean;
  mgr: AnyManager;
  pageId: string;
  suffix: string;
}

export default function Component({
  appUid,
  devMode,
  mgr,
  pageId,
  suffix,
}: Props): React.ReactElement | null {
  const classes = useStyles();
  const get = useStateXGetter();
  const [itemId, setId] = useState(() =>
    genId((get(designPath({ appUid, pageId })) ?? {}) as Page),
  );
  const set = useStateXSetter();
  const platform = usePlatform();
  const [, setActiveItem] = useStateXWritableSelector(activeItemSelector, {
    params: { appUid, pageId, treename: 'designer' },
    props: platform,
  });
  const mounted = useMounted();
  const ref = useIORef<HTMLDivElement>();
  const [item, setItem] = useState<AnyItem>(() => {
    // just used to derive the icon
    // actual item will be created by calling createItem on drag
    return {
      itemId,
      // @ts-ignore dynamic item
      props: { ...mgr.defaultValue },
      children: [],
      parent: '',
      // @ts-ignore dynamic item
      type: mgr.type,
    };
  });
  useDnD({
    appUid,
    pageId,
    item,
    ref,
    callback: () => {
      let page = get(designPath({ appUid, pageId })) as Page;
      page = { ...page };
      const { page: newPage } = createItem({
        platform,
        page,
        parentId: '',
        itemType: mgr.type,
        itemId,
        appUid,
        pageId,
      });
      set(designPath({ appUid, pageId }), newPage);
    },
    endCallback: () => {
      const page = get(designPath({ appUid, pageId })) as Page;
      if (page[itemId]) {
        setActiveItem({
          appUid,
          pageId,
          itemId,
          expand: false,
          treename: 'designer',
        });
        if (mounted.current) {
          const uid = genId(page);
          setId(uid);
          setItem({
            itemId: uid,
            // @ts-ignore dynamic item
            props: { ...mgr.defaultValue },
            children: [],
            parent: '',
            // @ts-ignore dynamic item
            type: mgr.type,
          });
        }
      }
    },
  });

  let icon = mgr.icon;
  if (typeof icon === 'function') {
    icon = icon(item);
  }

  return (
    <motion.div
      layout="position"
      layoutId={`${mgr.type}-${suffix}`}
      ref={devMode ? ref : undefined}
      data-testid={`comp-${mgr.type}`}
      className={clsx(classes.comp, devMode ? classes.devMode : null)}
      data-tip={`${devMode ? 'Double ' : ''}Click to learn more about ${
        mgr.name
      }`}
      onClick={
        devMode
          ? undefined
          : () => {
              startTransition(() => {
                set(sidebarComponentViewAtom, 'help');
                set(sidebarActiveComponentAtom, mgr.type);
              });
            }
      }
      onDoubleClick={() => {
        startTransition(() => {
          set(sidebarComponentViewAtom, 'help');
          set(sidebarActiveComponentAtom, mgr.type);
        });
      }}
    >
      <div className={classes.name} data-tip={mgr.name}>
        {mgr.name}
      </div>
      <div className={classes.icon}>
        <Badge
          color="primary"
          variant="dot"
          invisible={mgr.helpRenders.length === 0}
        >
          <Icon width={32} height={32} {...icon} />
        </Badge>
      </div>
    </motion.div>
  );
}
